// Teams list

import { Link } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { toast } from 'react-toastify';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import Select from './Select';
import CheckBox from './CheckBox';
import SetDiscount from './SetDiscount';
import MultiSelectInline from './MultiSelectInline';
import FilterInput from './FilterInput';
import GuidedTour from './GuidedTour';
import Tooltip from './Tooltip';
import Spinner from './Spinner';
import PaymentModalBody from './PaymentModalBody';
import PaymentInfo from './PaymentInfo';
import HeaderWithLines from './HeaderWithLines';
import TeamInviteButton from './TeamInviteButton';
import {
  tournamentTeamUpdate,
  userUpdate,
  userRemoveTeam,
} from '../utils/firebase';
import {
  useEntities,
  useTeamsDirectory,
  useTournamentPayments,
  useUserTournamentTeams,
} from '../utils/hooks';
import { getTeamDivision, isTooOld, toOptions, cityOf } from '../utils/misc';
import { UsersRoles } from '../utils/enums';
import PlayerStatus from './PlayerStatus';
import placeholderTeam from '../assets/images/placeholder-team.png';
import './TeamsList.scss';

const tourKey1 = 'teams-list-1-v1';
const steps1 = [
  {
    target: '.add-team',
    title: 'Add team',
    content: 'Click here to add your teams',
  },
];

const tourKey2 = 'teams-list-2-v1';
const steps2 = [
  {
    target: '.team:first-of-type',
    title: 'Open team',
    content: 'Here is your team. Click to view details',
  },
];

const TeamDivisionPlayers = ({ team, playersNum }) => (
  <>
    <p className={'division'}>{getTeamDivision(team)}</p>
    <p className={'division'}>{playersNum + ' players'}</p>
  </>
);

export default function TeamsList({ tournamentId, userId }) {
  const [showSelectTeams, setShowSelectTeams] = useState(false);
  const [admitted, setAdmitted] = useState({});
  const [spinner, setSpinner] = useState(false);
  const [removeTeamId, setRemoveTeamId] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');

  const manageTeamsBtnRef = useRef(null);

  const {
    user,
    usersById,
    tournamentsById,
    userIsAdmin,
    userIsCoach,
    userIsPlayer,
  } = useFirebase();
  const { openModal } = useModal();

  const paidByTeamId = useTournamentPayments(tournamentId);

  const tournament = tournamentsById.get(tournamentId);

  const teamsUser = useMemo(() => usersById.get(userId), [usersById, userId]);

  // Non-admin view of a tournament: only the user's own teams taking part in it
  // (division match, invited, and admitted for players/parents)
  const myTeamsMode = !!tournamentId && !userIsAdmin;
  const { isReady: isMyTeamsReady, teams: myTournamentTeams } =
    useUserTournamentTeams(tournamentId);

  // Three modes, three sources, all producing the same row shape:
  //  - a tournament seen by its own coach/player/parent — their teams in it;
  //  - somebody's card (`userId`) — exactly the teams on that person, by id;
  //  - the admin lists — the organization's directory, six documents instead
  //    of five hundred team reads.
  //
  // The directory is also what "Manage teams" offers, so an admin standing on
  // somebody's card reads it as well; a coach or player on their own card does
  // not, and sees no query for teams that are not theirs.
  const { entities: subjectTeams, isResolved: areSubjectTeamsResolved } =
    useEntities('teams', userId ? (teamsUser?.teams ?? []) : []);
  const {
    teams: directoryTeams,
    teamsById: directoryById,
    isLoaded: isDirectoryLoaded,
  } = useTeamsDirectory(!myTeamsMode && (!userId || userIsAdmin));

  const teams = useMemo(() => {
    if (myTeamsMode) return myTournamentTeams;
    if (userId) return subjectTeams.filter(Boolean);
    return directoryTeams;
  }, [myTeamsMode, myTournamentTeams, userId, subjectTeams, directoryTeams]);

  // Whichever of the three answered — used for the one place that resolves a
  // name by id, and to tell "still reading" from "no teams"
  const teamsById = useMemo(
    () =>
      userId || myTeamsMode
        ? new Map(teams.map((t) => [t.id, t]))
        : directoryById,
    [userId, myTeamsMode, teams, directoryById],
  );
  const isSourceReady = myTeamsMode
    ? isMyTeamsReady
    : userId
      ? !!teamsUser && areSubjectTeamsResolved
      : isDirectoryLoaded;

  useEffect(() => {
    if (!tournament?.teams) return;
    const admittedMap = tournament.teams.reduce((acc, team) => {
      acc[team.id] = team.admitted;
      return acc;
    }, {});
    setAdmitted(admittedMap);
  }, [tournament?.teams]);

  const onAdmittedClick = async (teamId, val) => {
    setAdmitted((prev) => ({
      ...prev,
      [teamId]: val,
    }));

    try {
      await tournamentTeamUpdate({
        id: tournamentId,
        team: teamId,
        admitted: val,
      });
    } catch (error) {
      setAdmitted((prev) => ({
        ...prev,
        [teamId]: !val,
      }));
      toast.error(error?.message ?? 'Failed to update the admission');
    }
  };

  const onPay = (e, teamId) => {
    e.stopPropagation();
    e.preventDefault();
    openModal({
      header: 'Payment',
      body: <PaymentModalBody team={teamId} tournament={tournamentId} />,
    });
  };

  const onSetDiscount = (e, team, discount) => {
    e.stopPropagation();
    e.preventDefault();
    openModal({
      header: 'Set discount',
      body: <SetDiscount id={tournamentId} team={team} discount={discount} />,
    });
  };

  const onBulkDiscount = (e) => {
    e.stopPropagation();
    e.preventDefault();
    openModal({
      header: 'Bulk discount',
      body: <SetDiscount id={tournamentId} discount={null} />,
    });
  };

  const handleSelectTeams = async (selectedTeamIds) => {
    if (!userId) return;

    await userUpdate({
      id: userId,
      teams: selectedTeamIds,
    });
  };

  const locations = useMemo(
    () => [
      'All locations',
      ...new Set(teams.map((team) => cityOf(team.location)).filter(Boolean)),
    ],
    [teams],
  );

  const divisions = [
    'All divisions',
    ...new Set(teams.map((team) => getTeamDivision(team))),
  ];

  const [location, setLocation] = useState(locations[0]);
  const [division, setDivision] = useState(divisions[0]);

  const addCol = tournamentId && userIsAdmin;
  // `invited`/`admitted` live in the tournaments/{id}/teams subcollection, which
  // arrives after the tournament doc. Hold the admin columns back until it does,
  // so invited teams don't flash as "Not invited" (and get re-invited by a click).
  const adminCols = addCol && tournament?.teamsLoaded === true;
  const payCol = tournamentId && (userIsAdmin || userIsCoach);
  const addTeam = !tournamentId && ((userIsAdmin && !userId) || userIsCoach);
  const manageTeams = !tournamentId && userIsAdmin && userId;

  const filteredSortedTeams = useMemo(() => {
    // Apply location filter
    const byLocation = teams.filter((team) => {
      if (location === 'All locations') return true;
      if (cityOf(team.location) === location) return true;
      return false;
    });

    // Apply division filter
    const byDivision = byLocation.filter((team) => {
      if (division === 'All divisions') return true;
      if (getTeamDivision(team) === division) return true;
      return false;
    });

    // Apply coach filter
    const byCoach = byDivision.filter((team) => {
      if (!userId) return true;
      if (teamsUser?.teams.includes(team.id)) return true;
      return false;
    });

    // Apply tournament division filter
    const byTournamentDivision = byCoach.filter((team) => {
      if (!tournament) return true;
      return (
        tournament.sports.includes(team.sport) &&
        tournament.genders.includes(team.gender) &&
        tournament.ages.includes(team.age)
      );
    });

    // Sorting logic: paid desc, division lexicographically, name asc
    const sorted = [...byTournamentDivision].sort((a, b) => {
      const diffPaid =
        (paidByTeamId.get(b.id) ?? 0) - (paidByTeamId.get(a.id) ?? 0);
      if (diffPaid !== 0) return diffPaid;

      const divA = getTeamDivision(a);
      const divB = getTeamDivision(b);
      const cmpDiv = divA.localeCompare(divB);
      if (cmpDiv !== 0) return cmpDiv;

      return a.name.localeCompare(b.name);
    });

    // Map to enriched objects so JSX doesn’t recalc repeatedly
    return sorted.map((team) => {
      const { id, name, imgThumbUrl, location: teamLocation } = team;
      const isDisabled =
        (userIsPlayer || userIsCoach) && !user.teams.includes(id);
      // Kept on the team document by a trigger; absent means zero, not unknown.
      // It used to be an O(teams x users) scan of the organization-wide list
      const playersNum = team.playersCount ?? 0;
      const paid = paidByTeamId.get(id) ?? 0;
      const teamEntry = tournament?.teams.find((t) => t.id === id);
      const discount = teamEntry?.discount || 0;
      const invited = teamEntry?.invited === true;

      const remaining = tournament?.fee - paid - discount;

      // `coachNames` is index-aligned with `coachIds` on the team document and
      // copied verbatim onto a directory row, so one expression serves all
      // three sources. An empty name means the user is gone — dropped rather
      // than rendered as a stray comma.
      const coaches = (team.coachNames || []).filter(Boolean).join(', ');

      const tooOld =
        userId && teamsUser?.role === UsersRoles.PLAYER
          ? isTooOld({
              birthDate: teamsUser.birthDate,
              age: team.age,
              cutOffDate: team.cutOffDate,
            })
          : false;

      return {
        team,
        derived: {
          isDisabled,
          playersNum,
          paid,
          discount,
          invited,
          remaining,
          coaches,
          imgThumbUrl,
          name,
          teamLocation,
          id,
          tooOld,
        },
      };
    });
  }, [
    teams,
    location,
    division,
    userId,
    teamsUser,
    paidByTeamId,
    userIsPlayer,
    userIsCoach,
    user,
    tournament,
  ]);

  // Stop spinner and reset removeTeamId if the target team is no longer in the list
  useEffect(() => {
    if (!spinner || !removeTeamId) return;
    if (
      !filteredSortedTeams.some(({ derived }) => derived.id === removeTeamId)
    ) {
      setSpinner(false);
      setRemoveTeamId(null);
    }
  }, [spinner, removeTeamId, filteredSortedTeams]);

  // const showRemoveCol = userId && (userIsAdmin || userIsCoach);

  const showRemoveCol =
    !!userId &&
    ((teamsUser?.role === UsersRoles.COACH && userIsAdmin) ||
      (teamsUser?.role === UsersRoles.PLAYER && (userIsAdmin || userIsCoach)));
  const removeLabel = `Remove ${
    teamsUser?.role === UsersRoles.COACH ? 'coach' : 'player'
  } from the team`;

  // Remove the selected user from the selected team
  const handleTeamRemove = (e, teamId) => {
    e.preventDefault();
    e.stopPropagation();

    const userName = teamsUser?.name;
    const teamName = teamsById.get(teamId)?.name;
    const roleLabel =
      teamsUser?.role === UsersRoles.COACH
        ? 'coach'
        : teamsUser?.role === UsersRoles.PLAYER
          ? 'player'
          : 'user';

    const onRemove = async () => {
      try {
        setSpinner(true);
        setRemoveTeamId(teamId);
        await userRemoveTeam({
          id: userId,
          team: teamId,
        });
      } catch (error) {
        setSpinner(false);
        toast.error(
          error?.message ?? `Failed to remove ${roleLabel} from the team`,
        );
      }
    };

    openModal({
      header: `Remove ${roleLabel} from team`,
      body: (
        <p className='font-16'>
          {`Are you sure you want to remove `}
          <span className='font-medium'>{userName}</span>
          {` from `}
          <span className='font-medium'>{teamName}?</span>
        </p>
      ),
      buttons: [
        {
          text: 'Cancel',
          className: 'bw',
        },
        {
          text: 'Remove',
          className: 'danger',
          onClick: onRemove,
        },
      ],
    });
  };

  // Search values: name, location, division
  const searchValues = useMemo(
    () =>
      filteredSortedTeams.map(({ team, derived }) => [
        derived.name,
        derived.teamLocation,
        getTeamDivision(team),
      ]),
    [filteredSortedTeams],
  );

  const displayedTeams = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return filteredSortedTeams;
    return filteredSortedTeams.filter((_, i) =>
      searchValues[i].some((val) => val && val.toLowerCase().includes(q)),
    );
  }, [filteredSortedTeams, searchValues, searchQuery]);

  // The tour anchors on real DOM: the Add team button is always there, the row
  // only once the teams source has answered
  const hasNoTeams = user.teams?.length === 0;
  const runTour = hasNoTeams ? isSourceReady : displayedTeams.length > 0;

  // Top-level grouping for the admin tournament view: invited teams first.
  // A single header above the only non-empty group is noise, so skip it.
  const teamGroups = useMemo(() => {
    const flat = [{ key: 'all', header: null, items: displayedTeams }];
    if (!adminCols) return flat;

    const invitedTeams = displayedTeams.filter(
      ({ derived }) => derived.invited,
    );
    const notInvitedTeams = displayedTeams.filter(
      ({ derived }) => !derived.invited,
    );
    if (!invitedTeams.length || !notInvitedTeams.length) return flat;

    return [
      { key: 'invited', header: 'Invited', items: invitedTeams },
      { key: 'not-invited', header: 'Not invited', items: notInvitedTeams },
    ];
  }, [adminCols, displayedTeams]);

  const renderTeam = ({ team, derived }) => {
    const {
      isDisabled,
      playersNum,
      paid,
      discount,
      invited,
      remaining,
      coaches,
      imgThumbUrl,
      name,
      teamLocation,
      id,
    } = derived;

    const isTheCoachOfTeam = userIsCoach && user.teams?.includes(id);
    const canRemoveFromThisTeam =
      !!userId &&
      ((teamsUser?.role === UsersRoles.COACH && userIsAdmin) ||
        (teamsUser?.role === UsersRoles.PLAYER &&
          (userIsAdmin || isTheCoachOfTeam)));

    return (
      <Link
        to={'/teams/' + id}
        className={'team' + (isDisabled ? ' disabled' : '')}
        onClick={isDisabled ? (e) => e.preventDefault() : undefined}
        key={id}
      >
        <div
          className='col-1 bg-image wh-64'
          style={{
            backgroundImage: `url(${imgThumbUrl || placeholderTeam})`,
          }}
        />
        <div className='col-23'>
          <div className='img-name-wrap'>
            <div
              className='bg-image wh-32'
              style={{
                backgroundImage: `url(${imgThumbUrl || placeholderTeam})`,
              }}
            />
            <p className='font-medium upper'>{name}</p>
          </div>
          <p>
            <span className='fa-location gray-800' /> {teamLocation}
          </p>
          <p>{coaches}</p>
          <TeamDivisionPlayers team={team} playersNum={playersNum} />
          {derived.tooOld && (
            <div className='status-inline'>
              <PlayerStatus tooOld />
            </div>
          )}
        </div>
        <div className='col-5'>
          <TeamDivisionPlayers team={team} playersNum={playersNum} />
        </div>
        {userId && (
          <div className='col-status'>
            {derived.tooOld && <PlayerStatus tooOld />}
          </div>
        )}
        {payCol && (
          <div className='col-78'>
            <PaymentInfo
              paid={paid}
              discount={userIsAdmin ? undefined : discount}
              remaining={remaining}
              deposit={tournament?.deposit}
              disabled={!invited}
            >
              {userIsAdmin ? (
                <button
                  className={`small${discount ? '' : ' bw'}`}
                  disabled={!invited}
                  onClick={(e) => {
                    onSetDiscount(e, id, discount);
                  }}
                >
                  {discount ? 'Discount $' + discount : 'Set discount'}
                </button>
              ) : (
                invited &&
                remaining > 0 && (
                  <button className='small bw' onClick={(e) => onPay(e, id)}>
                    Pay
                  </button>
                )
              )}
            </PaymentInfo>
          </div>
        )}
        {adminCols && (
          <>
            <div
              className={'col-9' + (invited ? '' : ' disabled')}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <CheckBox
                ariaLabel={`Admit ${name}`}
                onChange={(val) => onAdmittedClick(id, val)}
                value={!!admitted[id]}
                disabled={!invited}
              />
            </div>
            <div
              className='col-invite'
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <TeamInviteButton
                tournamentId={tournamentId}
                teamId={id}
                teamName={name}
                invited={invited}
              />
            </div>
          </>
        )}
        {showRemoveCol && (
          <div className='col-6'>
            {canRemoveFromThisTeam && (
              <Tooltip label={removeLabel}>
                <button
                  className='transparent small'
                  onClick={(e) => handleTeamRemove(e, id)}
                  aria-label={removeLabel}
                >
                  <p className='fa-trash danger' />
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </Link>
    );
  };

  return (
    <GuidedTour
      steps={userIsCoach && !myTeamsMode ? (hasNoTeams ? steps1 : steps2) : []}
      run={runTour}
      continuous={false}
      tourKey={hasNoTeams ? tourKey1 : tourKey2}
    >
      <div className='teams-list'>
        <div className='sticky-top'>
          {!myTeamsMode && (
            <div className='controls'>
              <ul className='filters'>
                <li>
                  <FilterInput
                    className='search'
                    placeholder='Search'
                    value={searchQuery}
                    onChange={setSearchQuery}
                    small
                  />
                </li>
                {!userIsCoach && (
                  <>
                    <li>
                      <Select
                        options={locations}
                        value={location}
                        onChange={setLocation}
                        placeholder='Location'
                        small
                        className='location'
                      />
                    </li>
                    <li>
                      <Select
                        options={divisions}
                        value={division}
                        onChange={setDivision}
                        placeholder='Division'
                        small
                        className='sport'
                      />
                    </li>
                  </>
                )}
              </ul>
              {addTeam && (
                <Link
                  to='/teams/new?edit=true'
                  className='add-team button small bw'
                >
                  Add team
                </Link>
              )}
              {manageTeams && (
                <div className='manage-teams'>
                  <button
                    className='small bw'
                    onClick={() => setShowSelectTeams(true)}
                    ref={manageTeamsBtnRef}
                  >
                    Manage teams
                  </button>
                  <MultiSelectInline
                    className='select-team'
                    header='Choose teams'
                    options={toOptions(directoryTeams)}
                    value={teamsUser?.teams || []}
                    small
                    show={showSelectTeams}
                    onChange={handleSelectTeams}
                    onClose={() => setShowSelectTeams(false)}
                    anchorRef={manageTeamsBtnRef}
                  />
                </div>
              )}
              {addCol && (
                <button className='small bw' onClick={onBulkDiscount}>
                  Bulk discount
                </button>
              )}
            </div>
          )}
          {!(searchQuery.trim() && displayedTeams.length === 0) && (
            <ul className='header tab-header'>
              <li className='col-1'></li>
              <li className='col-23'>
                Team, Location, Coaches
                <span className='division'>, Division, Players</span>
                {userId && <span className='status-inline'>, Status</span>}
                {payCol && <span className='payment-inline'>, Payment</span>}
              </li>
              <li className='col-5'>Division, Players</li>
              {userId && <li className='col-status'>Status</li>}
              {payCol && <li className='col-78'>Payment</li>}
              {adminCols && (
                <>
                  <li className='col-9'>Admitted</li>
                  <li className='col-invite'>Invite</li>
                </>
              )}
              {showRemoveCol && <li className='col-6'>Delete</li>}
            </ul>
          )}
        </div>
        {searchQuery.trim() && displayedTeams.length === 0 && (
          <h5 className='name'>No results found for your search</h5>
        )}
        {teamGroups.map(({ key, header, items }) => (
          <Fragment key={key}>
            {header && <HeaderWithLines>{header}</HeaderWithLines>}
            {items.map(renderTeam)}
          </Fragment>
        ))}
        {/* Without the readiness check the list renders empty first and fills
            in a moment later, which reads as "you have no teams here" */}
        <Spinner show={spinner || !isSourceReady} />
      </div>
    </GuidedTour>
  );
}
